import { ReactElement } from 'react';

import { ReactComponent as BackgroundCircles } from '@backrs_ui/common-ui/src/assets/images/background-circles.svg';

import { Box, Dialog, DialogProps, SxProps, Theme } from '@mui/material';

import { AppIcon, AppIcons } from '../designSystem/icons/AppIcon';
import { FeaturedIcon } from '../designSystem/icons/FeaturedIcon';

export type InfoModalProps = Readonly<{
  open: boolean;
  onClose?: () => void;
  width?: DialogProps['maxWidth'];
  /** if you do not pass in headerContent, the FeaturedIcon will not be rendered, and the close icon will be absolutely positioned in the top right corner */
  headerContent?: ReactElement;
  bodyContent: ReactElement;
  dialogSx?: SxProps<Theme>;
  icon?: AppIcons;
}>;

export const InfoView = ({
  onClose,
  headerContent,
  bodyContent,
  icon = 'InfoCircle',
}: Omit<InfoModalProps, 'open' | 'width' | 'dialogSx'>) => {
  return (
    <Box sx={containerSx}>
      <Box sx={headerSx(!!headerContent)}>
        {!!headerContent && (
          <Box sx={backgroundCirclesSx}>
            <BackgroundCircles />
          </Box>
        )}

        <Box sx={iconsSx(!!headerContent)}>
          {!!headerContent && (
            <FeaturedIcon iconName={icon} size="lg" color="Colors/Foreground/fg-secondary" />
          )}
          {onClose && (
            <Box sx={closeIconSx(!!headerContent)} onClick={onClose}>
              <AppIcon iconName="XClose" diameter="20px" color="Colors/Foreground/fg-quaternary" />
            </Box>
          )}
        </Box>

        {headerContent}
      </Box>

      <Box>{bodyContent}</Box>
    </Box>
  );
};

export const InfoModal = ({
  open,
  onClose,
  width = 'md',
  headerContent,
  bodyContent,
  dialogSx,
  icon,
}: InfoModalProps) => {
  return (
    <Dialog
      className="info-modal-dialog"
      open={open}
      onClose={onClose}
      sx={[dialogStyle, dialogSx].filter(Boolean) as SxProps}
      maxWidth={width}
      fullWidth
    >
      <InfoView
        onClose={onClose}
        headerContent={headerContent}
        bodyContent={bodyContent}
        icon={icon}
      />
    </Dialog>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  p: backrs.spacing['spacing-3xl'],
  overflow: 'hidden',
  overflowY: 'auto',
});

const headerSx =
  (hasHeaderContent: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    position: 'relative',
    width: '100%',
    ...(hasHeaderContent && {
      mb: backrs.spacing['spacing-2xl'],
    }),
  });

const iconsSx =
  (hasHeaderContent: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    zIndex: 0,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',

    ...(hasHeaderContent && {
      mb: backrs.spacing['spacing-xl'],
      justifyContent: 'space-between',
    }),
  });

const closeIconSx =
  (hasHeaderContent: boolean): SxProps<Theme> =>
  () => ({
    cursor: 'pointer',
    justifySelf: 'flex-end',

    ...(!hasHeaderContent && {
      position: 'absolute',
      top: 0,
      right: 0,
      zIndex: 1000,
    }),
  });

const backgroundCirclesSx: SxProps<Theme> = {
  position: 'absolute',
  top: -(336 * 0.425),
  left: -(336 * 0.425),
  zIndex: 1,
};

const dialogStyle: SxProps<Theme> = ({ backrs }) => ({
  zIndex: 99999,
  '& .MuiDialog-paper': {
    borderRadius: backrs.radius['radius-xl'],
    boxShadow: backrs.shadows['shadow-xl'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  },
});
